import React, { useContext } from "react";
import PropTypes from "prop-types";
import getCarCostOfOwnership from "../../functions/vehicle/CostOfOwnership/getCarCostOfOwnership";
import UserPrefsContext from "../../context/UserPrefs/UserPrefsContext";
import { FormatAsDollars, FormatCarName } from "../../utils/Helpers/Format";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar as HorizontalBar } from "react-chartjs-2";
import { FormattedMessage, useIntl } from "react-intl";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

const CHART_JS_OPTIONS = {
     indexAxis: "y",
     maintainAspectRatio: false,
     scales: {
          x: {
               stacked: true,
               ticks: {
                    callback: function (value) {
                         return FormatAsDollars(value);
                    },
                    beginAtZero: true
               }
          },
          y: {
               stacked: true,
               ticks: {
                    font: { weight: 700 },
                    color: "#191919"
               }
          }
     },
     layout: {
          padding: {
               right: 75
          }
     },
     plugins: {
          tooltip: {
               callbacks: {
                    label: function (context) {
                         const value = FormatAsDollars(context.dataset.data[context.dataIndex]);
                         const label = context.dataset.label || "";
                         return " " + label + ": " + value;
                    },
                    title: function (tooltipItems) {
                         const total = tooltipItems.reduce((acc, item) => acc + item.parsed.x, 0);
                         return tooltipItems[0].label + ": " + FormatAsDollars(total);
                    }
               }
          },
          legend: {
               position: "bottom",
               onClick: function (e) {
                    e.stopPropagation();
               }
          },
          datalabels: {
               formatter: (value, ctx) => {
                    let datasets = ctx.chart.data.datasets;
                    if (ctx.datasetIndex === datasets.length - 1) {
                         let sum = 0;
                         datasets.forEach((dataset) => {
                              sum += dataset.data[ctx.dataIndex];
                         });
                         return FormatAsDollars(sum);
                    } else {
                         return "";
                    }
               },
               align: "end",
               anchor: "end",
               color: "#191919",
               font: {
                    weight: 400
               }
          }
     }
};

const MonthlyFuelCostChart = ({ cars, forceUserPrefsPresets, hasExtraPadding, comparison }) => {
     const intl = useIntl();
     const userPrefs = useContext(UserPrefsContext);

     if (!cars || cars.length === 0) return null;

     cars = cars.filter((car) => {
          return car !== null;
     });

     const carsCosts = cars.map((car) => {
          return getCarCostOfOwnership(car, userPrefs);
     });

     const sortedCarCosts = [...carsCosts].sort((carCost1, carCost2) => {
          let cost1 = carCost1.electricity.perMonth + carCost1.gasoline.perMonth;
          let cost2 = carCost2.electricity.perMonth + carCost2.gasoline.perMonth;
          return cost1 - cost2;
     });

     const subTitle = (cars) => {
          if (cars.length === 2) {
               const costDelta =
                    carsCosts[1].electricity.perMonth +
                    carsCosts[1].gasoline.perMonth -
                    carsCosts[0].electricity.perMonth -
                    carsCosts[0].gasoline.perMonth;
               const costDeltaText =
                    costDelta < 0
                         ? intl.formatMessage({
                                id: "graph.moreExpensive",
                                defaultMessage: "more expensive"
                           })
                         : intl.formatMessage({
                                id: "graph.cheaper",
                                defaultMessage: "cheaper"
                           });

               return (
                    <p
                         className="h3 my-3 graph-sub-title"
                         style={{
                              fontFamily: "TT Norms Pro, Roboto",
                              fontWeight: "400",
                              fontSize: "16px"
                         }}>
                         <FormattedMessage
                              id="graph.monthlyCostToFill.subTitle"
                              defaultMessage="The {carName} is {style} {costDeltaText} to fill up monthly"
                              description="Graph Sub Title"
                              values={{
                                   carName: FormatCarName(cars[0]),
                                   style: (
                                        <span style={{ color: "#EB1700" }}>{FormatAsDollars(Math.abs(costDelta))}</span>
                                   ),
                                   costDeltaText: costDeltaText
                              }}
                         />
                    </p>
               );
          } else {
               return (
                    <p
                         className="h3 my-3 graph-sub-title"
                         style={{
                              fontFamily: "Roboto, TT Norms Pro",
                              fontWeight: "400",
                              fontSize: "16px"
                         }}>
                         <FormattedMessage
                              id="graph.monthlyCostToFill.subTitleThreeCars"
                              defaultMessage="The {carName} is cheapest to fill up monthly"
                              description="Graph Sub Title"
                              values={{
                                   carName: FormatCarName(sortedCarCosts[0].car)
                              }}
                         />
                    </p>
               );
          }
     };

     let title = <>{subTitle(cars)}</>;

     const carNames = comparison
          ? cars.map((car) => FormatCarName(car))
          : [
                 [FormatCarName(cars[0]), "(Your selection)"],
                 [FormatCarName(cars[1]), "(Similar gas vehicle)"]
            ];

     const chartData = {
          labels: carNames,
          datasets: [
               {
                    label: intl.formatMessage({
                         id: "graph.costOfOwnership.chart.electricity",
                         defaultMessage: "Electricity"
                    }),
                    backgroundColor: `#FF3008`,
                    data: carsCosts.map((carCost) => carCost.electricity.perMonth)
               },
               {
                    label: intl.formatMessage({
                         id: "graph.costOfOwnership.chart.gasoline",
                         defaultMessage: "Gasoline"
                    }),
                    backgroundColor: `#4C0C3A`,
                    data: carsCosts.map((carCost) => carCost.gasoline.perMonth)
               }
          ]
     };

     return (
          <div
               className="input-well text-center chartContainer"
               style={hasExtraPadding ? { padding: "2.5rem" } : { paddfing: "1rem" }}>
               {title}
               <div className="d-none d-lg-block d-xl-block">
                    <div>
                         <HorizontalBar data={chartData} height={212} options={CHART_JS_OPTIONS} />
                    </div>
               </div>
               {hasExtraPadding && <br />}
          </div>
     );
};

export default MonthlyFuelCostChart;

MonthlyFuelCostChart.propTypes = {
     car: PropTypes.object,
     forceUserPrefsPresets: PropTypes.bool,
     hasExtraPadding: PropTypes.bool
};
